import { ITransformData } from "@base/math/TransformData";
import { PartialRecord } from "@base/util/TypeUtil";
import { KederSize } from "@biz/definitions/KederDefinitions";
import { IProductData } from "@biz/definitions/ProductDefinitions";
import { EdgeName } from "@biz/definitions/SunSailDefinitions";
import { IClothItemData } from "@biz/logic/ClothData";
import { IProductDescriptor } from "@biz/ProductDescriptor";

// export type NetSides = "ab" | "bc" | "cd" | "ad" | "ac"
export type NetShapeType = "triangle"  // [docs/images/NetTriangle.jpg]
  | "rectangle"// [docs/images/NetRectangle.jpg]
  // | "trapezoid" // [docs/images/NetTrapez.jpg]
  | "quad";  // [docs/images/NetQuad.jpg]

export type NetPointName = "a" | "b" | "c" | "d";
export function getNetPointIndex(pointName: NetPointName): number {
  switch (pointName) {
    case "a": return 0;
    case "b": return 1;
    case "c": return 2;
    case "d": return 3;
  }
  throw new Error("getNetPointIndex: Not supported so far!" + pointName);
}

export function getNetPointNameForIndex(index: number): NetPointName {
  switch (index) {
    case 0: return "a";
    case 1: return "b";
    case 2: return "c";
    case 3: return "d";
  }
  throw new Error("getNetPointNameForIndex: Not supported so far!" + index);
}

export function getNetEdgePointName(edgeName: EdgeName, index: number): NetPointName {
  return edgeName[index].toLowerCase() as NetPointName
}

export function getNetEdgeIndices(edgeName: EdgeName): number[] {
  return [
    getNetPointIndex(getNetEdgePointName(edgeName, 0)),
    getNetPointIndex(getNetEdgePointName(edgeName, 1))
  ]
}

export interface INetDataSharedWithSC {
  pd: IProductDescriptor;
  shapeType: NetShapeType;
  positionMode?: PositionMode;
  dimensions: PartialRecord<EdgeName, number>;
  edgeConfigMode: NetMultiConfigMode;
  edges: PartialRecord<EdgeName, INetEdgeData>;
  pointConfigMode: NetMultiConfigMode;
  points: PartialRecord<NetPointName, INetPointData>;
  eyeletType?: NetEyeletType;
}

export interface INetData extends IProductData, IClothItemData, INetDataSharedWithSC {
  itemType: "net";

  transform: ITransformData;
}

export type NetSideFinishing = "pe" | "peVelcro" | "profi" | "narrow" | "overlock" | "cut" | "keder" | "hemstitch"

export type NetMultiConfigMode = "all" | "individual";
export const netMultiConfigModes: NetMultiConfigMode[] = ["all", "individual"];

export type NetEdgeColorType = "white" | "black" | "grey" | "beige"
export const netEdgeColorTypes: NetEdgeColorType[] = ["black", "grey", "beige", "white"];

export type NetEyeletType = "galvanized" | "stainless"
export const netEyeletTypes: NetEyeletType[] = ["galvanized", "stainless"];

export type NetVelcroColor = "white" | "black"
export const netVelcroColors: NetVelcroColor[] = ["white", "black"];

export type NetVelcroType = "loop" | "hook";
export type NetVelcroSide = "front" | "back";
export type NetVelcroSideSelection = NetVelcroSide | "both";

export type NetHemstitchSizeOption = "standard" | "custom";
export const netHemstitchSizeOptions: NetHemstitchSizeOption[] = ["standard", "custom"];

export type NetEyeletPositionMode = "none" | "standard" | "countPerSide" | "distance" | "corner"
export type NetEyeletDistribution = "standard" | "evenly" | "corner";

export type NetEyeletCornerOverride = "keep" | "none" | "singleEyelet" | "doubleEyelet" | "dring";


export type PositionMode = "bottom" | "mount" | "net";
export type EdgeMountItemType = "eyelet" | "strap" | "keder";

export interface INetEdgeData {
  sideFinishingType?: NetSideFinishing;
  edgeColor?: NetEdgeColorType;

  eyeletPositionMode?: NetEyeletPositionMode;
  eyeletUserCount?: number;
  eyeletUserDistance?: number;

  velcroColor?: NetVelcroColor;
  velcroSide?: NetVelcroSideSelection;
  velcroTypeFront?: NetVelcroType;
  velcroTypeBack?: NetVelcroType;

  hemstitchSizeOption?: NetHemstitchSizeOption;
  hemstitchUserSize?: number;

  kederSize?: KederSize;
  kederRail?: string;
}

export interface INetPointData {
  eyeletOverride?: NetEyeletCornerOverride;
}